import type { WorkOrderActKc2Data, WorkOrderOtherPriceData, WorkOrderPriceData, WorkOrderPriceItem } from 'gql/graphql';
import { ActKc2StatusEnum, WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';

import { useMemo } from 'react';

export enum DeletableWorkOrderItemsStatus {
    noDeletable = 'noDeletable',
    deletable = 'deletable',
    partlyDeletable = 'partlyDeletable',
}

interface IDeletableMessage {
    message: string;
    itemsIds: number[];
}

export interface IUseDeletableWorkOrderItemsResponse {
    status: DeletableWorkOrderItemsStatus;
    deletableIds?: number[];
    messages?: Record<WorkOrderPriceTypeEnum, IDeletableMessage>;
}

const mtrMessage = 'Имеется фактическое выполнение. Чтобы удалить строку, очистите значения в ячейках Акта';
const message =
    'Имеется фактическое выполнение. Чтобы удалить строку, очистите значения в ячейках Акта для расценки и дочерних сущностей';

const defaultMessages: Record<WorkOrderPriceTypeEnum, IDeletableMessage> = {
    [WorkOrderPriceTypeEnum.Mtr]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrContractorEquipment]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrContractorMaterial]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrCustomerEquipment]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.MtrCustomerMaterial]: {
        message: mtrMessage,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Uer]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Coefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.ConstraintCoefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.TechPartCoefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.None]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Object]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.RootObject]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.DismantlingCoefficient]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.Other]: {
        message,
        itemsIds: [],
    },
    [WorkOrderPriceTypeEnum.OtherGroup]: {
        message,
        itemsIds: [],
    },
};

export const useDeletableWorkOrderItems = (
    data: WorkOrderPriceData[] | WorkOrderPriceData | WorkOrderOtherPriceData | WorkOrderPriceItem,
    actKc2Data: WorkOrderActKc2Data[],
    workOrderStatus?: WorkOrderStatusEnum,
): IUseDeletableWorkOrderItemsResponse =>
    useMemo(() => {
        if (workOrderStatus === WorkOrderStatusEnum.OnFormation) {
            return { status: DeletableWorkOrderItemsStatus.deletable };
        }

        if (workOrderStatus && [WorkOrderStatusEnum.Completed, WorkOrderStatusEnum.Delayed].includes(workOrderStatus)) {
            return { status: DeletableWorkOrderItemsStatus.noDeletable };
        }

        const hasOnCheckOrInProgressActs = !!actKc2Data?.find((a) =>
            [ActKc2StatusEnum.InProgress, ActKc2StatusEnum.OnCheck].includes(a.status),
        );

        if (!hasOnCheckOrInProgressActs) {
            return { status: DeletableWorkOrderItemsStatus.noDeletable };
        }

        const deletableIds: number[] = [];
        const messages = { ...defaultMessages };

        const doCheck = (item: WorkOrderPriceData) => {
            if (item?.isActKC2Price) {
                const isRatioItem = [
                    WorkOrderPriceTypeEnum.ConstraintCoefficient,
                    WorkOrderPriceTypeEnum.TechPartCoefficient,
                ].includes(item.type);

                if ((item.hasEmptyActAcceptedVolume && item.hasEmptyActPlanVolume) || isRatioItem) {
                    deletableIds.push(item.id);
                    messages[item.type].itemsIds = messages[item.type].itemsIds.filter(
                        (messagesItem) => messagesItem !== item.id,
                    );
                } else {
                    messages[item.type].itemsIds.push(item.id);
                }
            }
        };

        if (Array.isArray(data)) {
            data.forEach((item) => {
                doCheck(item);
            });
        } else {
            doCheck(data as WorkOrderPriceData);
        }

        return { status: DeletableWorkOrderItemsStatus.partlyDeletable, deletableIds, messages };
    }, [actKc2Data, data, workOrderStatus]);
